import React, { useState, useEffect, useCallback } from 'react';
import {
  Clock, Calendar, FileText, Eye, AlertTriangle,
  ChevronDown, ChevronUp, RefreshCw, Lock, Unlock, Building2,
  User, CheckCircle2, XCircle, MessageSquare, FileSignature,
  AlertCircle, ArrowRight, ExternalLink, Flag
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import AEDIcon from '@/components/icons/AEDIcon';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import api from '@/services/api';

// ============ CONSTANTS ============

const EVENT_TYPE_CONFIG = {
  // Deadlines
  DEADLINE_CREATED: { icon: Clock, color: 'text-blue-400', bg: 'bg-blue-600/20' },
  DEADLINE_COMPLETED: { icon: CheckCircle2, color: 'text-green-400', bg: 'bg-green-600/20' },
  DEADLINE_OVERDUE: { icon: AlertTriangle, color: 'text-red-400', bg: 'bg-red-600/20' },
  DEADLINE_EXTENDED: { icon: Clock, color: 'text-gold-400', bg: 'bg-gold-600/20' },
  
  // Viewings
  VIEWING_REQUESTED: { icon: Eye, color: 'text-blue-400', bg: 'bg-blue-600/20' },
  VIEWING_CONFIRMED: { icon: CheckCircle2, color: 'text-green-400', bg: 'bg-green-600/20' },
  VIEWING_COMPLETED: { icon: Eye, color: 'text-green-400', bg: 'bg-green-600/20' },
  VIEWING_CANCELLED: { icon: XCircle, color: 'text-red-400', bg: 'bg-red-600/20' },
  
  // Offers
  OFFER_SUBMITTED: { icon: AEDIcon, color: 'text-green-400', bg: 'bg-green-600/20' },
  OFFER_COUNTERED: { icon: AEDIcon, color: 'text-gold-400', bg: 'bg-gold-600/20' },
  OFFER_ACCEPTED: { icon: CheckCircle2, color: 'text-green-400', bg: 'bg-green-600/20' },
  OFFER_REJECTED: { icon: XCircle, color: 'text-red-400', bg: 'bg-red-600/20' },
  OFFER_WITHDRAWN: { icon: XCircle, color: 'text-gray-400', bg: 'bg-gray-600/20' },
  
  // Amendments
  AMENDMENT_CREATED: { icon: FileSignature, color: 'text-purple-400', bg: 'bg-purple-600/20' },
  AMENDMENT_SIGNED: { icon: FileSignature, color: 'text-blue-400', bg: 'bg-blue-600/20' },
  AMENDMENT_FULLY_EXECUTED: { icon: CheckCircle2, color: 'text-green-400', bg: 'bg-green-600/20' },
  AMENDMENT_VOIDED: { icon: XCircle, color: 'text-red-400', bg: 'bg-red-600/20' },
  
  // Conditions
  CONDITION_CREATED: { icon: AlertCircle, color: 'text-gold-400', bg: 'bg-gold-600/20' },
  CONDITION_CONFIRMED: { icon: CheckCircle2, color: 'text-green-400', bg: 'bg-green-600/20' },
  CONDITION_REJECTED: { icon: XCircle, color: 'text-red-400', bg: 'bg-red-600/20' },
  
  // Payments
  PAYMENT_STRUCTURE_CREATED: { icon: AEDIcon, color: 'text-green-400', bg: 'bg-green-600/20' },
  INSTALLMENT_PAID: { icon: CheckCircle2, color: 'text-green-400', bg: 'bg-green-600/20' },
  PAYMENT_CHANGE_REQUESTED: { icon: AEDIcon, color: 'text-gold-400', bg: 'bg-gold-600/20' },
  PAYMENT_CHANGE_APPROVED: { icon: CheckCircle2, color: 'text-green-400', bg: 'bg-green-600/20' },
  
  // Deal State
  DEAL_CREATED: { icon: Building2, color: 'text-blue-400', bg: 'bg-blue-600/20' },
  DEAL_STATE_CHANGED: { icon: ArrowRight, color: 'text-purple-400', bg: 'bg-purple-600/20' },
  DEAL_FROZEN: { icon: Lock, color: 'text-red-400', bg: 'bg-red-600/20' },
  DEAL_UNFROZEN: { icon: Unlock, color: 'text-green-400', bg: 'bg-green-600/20' },
  
  // Dispute
  DISPUTE_OPENED: { icon: AlertTriangle, color: 'text-red-400', bg: 'bg-red-600/20' },
  DISPUTE_RESOLVED: { icon: CheckCircle2, color: 'text-green-400', bg: 'bg-green-600/20' },
  
  // Default
  DEFAULT: { icon: Clock, color: 'text-gray-400', bg: 'bg-gray-600/20' }
};

const DEAL_STATE_COLORS = {
  deal_offer_accepted: 'bg-green-600/20 text-green-400 border-green-600/30',
  form_f_signed: 'bg-blue-600/20 text-blue-400 border-blue-600/30',
  deposit_collected: 'bg-purple-600/20 text-purple-400 border-purple-600/30',
  noc_submitted: 'bg-gold-600/20 text-gold-400 border-gold-600/30',
  mortgage_processing: 'bg-orange-600/20 text-orange-400 border-orange-600/30',
  transfer_pending: 'bg-cyan-600/20 text-cyan-400 border-cyan-600/30',
  completed: 'bg-green-600/20 text-green-400 border-green-600/30',
  terminated: 'bg-red-600/20 text-red-400 border-red-600/30',
  dispute_mode: 'bg-red-600/20 text-red-400 border-red-600/30'
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-AE', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return 'N/A';
  return new Intl.NumberFormat('en-AE', {
    style: 'currency',
    currency: 'AED',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

// ============ SUB-COMPONENTS ============

const TimelineEvent = ({ event, onOpenCondition, onOpenDispute }) => {
  const config = EVENT_TYPE_CONFIG[event.event_type] || EVENT_TYPE_CONFIG.DEFAULT;
  const Icon = config.icon;
  
  return (
    <div className="flex gap-4 relative">
      {/* Timeline Line */}
      <div className="absolute left-5 top-10 bottom-0 w-px bg-white/10" />
      
      {/* Icon */}
      <div className={`relative z-10 p-2 rounded-lg ${config.bg} shrink-0`}>
        <Icon className={`w-4 h-4 ${config.color}`} />
      </div>
      
      {/* Content */}
      <div className="flex-1 pb-6">
        <div className="p-3 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 transition-all">
          <div className="flex items-center justify-between">
            <p className={`font-medium text-sm ${config.color}`}>
              {event.event_type?.replace(/_/g, ' ')}
            </p>
            <span className="text-xs text-muted-foreground">
              {formatDate(event.timestamp)}
            </span>
          </div>
          
          {event.metadata?.reason && (
            <p className="text-sm text-muted-foreground mt-1">
              {event.metadata.reason}
            </p>
          )}
          
          {event.actor?.email && (
            <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
              <User className="w-3 h-3" />
              {event.actor.email}
            </p>
          )}
          
          {/* Quick Actions based on event type */}
          {event.event_type === 'CONDITION_CREATED' && (
            <Button
              size="sm"
              variant="ghost"
              className="mt-2 h-7 text-xs text-gold-400 hover:text-gold-300"
              onClick={() => onOpenCondition(event.metadata?.condition_id)}
            >
              Open Condition <ExternalLink className="w-3 h-3 ml-1" />
            </Button>
          )}
          
          {event.event_type === 'DISPUTE_OPENED' && (
            <Button
              size="sm"
              variant="ghost"
              className="mt-2 h-7 text-xs text-red-400 hover:text-red-300"
              onClick={() => onOpenDispute(event.entity?.id)}
            >
              Open Dispute <ExternalLink className="w-3 h-3 ml-1" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

const DealCard = ({ deal, onSelect, isSelected }) => {
  const stateColor = DEAL_STATE_COLORS[deal.deal_state] || DEAL_STATE_COLORS.deal_offer_accepted;
  
  return (
    <div
      className={`p-4 rounded-lg border cursor-pointer transition-all ${
        isSelected 
          ? 'border-primary bg-primary/10' 
          : 'border-white/10 bg-white/5 hover:border-white/20'
      }`}
      onClick={() => onSelect(deal)}
      data-testid={`deal-card-${deal.id}`}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="font-medium text-sm">
            {deal.property_title || 'Untitled Property'}
          </p>
          <p className="text-xs text-muted-foreground font-mono">
            {deal.id?.slice(0, 8).toUpperCase()}
          </p>
        </div>
        <Badge className={stateColor}>
          {deal.deal_state?.replace(/_/g, ' ')}
        </Badge>
      </div>
      
      <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
        <span className="flex items-center gap-1">
          <AEDIcon className="w-3 h-3" />
          {formatCurrency(deal.agreed_price)}
        </span>
        <span className="flex items-center gap-1">
          <Calendar className="w-3 h-3" />
          {formatDate(deal.created_at)}
        </span>
      </div>
    </div>
  );
};

// ============ MAIN COMPONENT ============

export default function DealTimelineViewer() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [deals, setDeals] = useState([]);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [stateFilter, setStateFilter] = useState('all');
  
  // Admin Action Dialogs
  const [freezeDialog, setFreezeDialog] = useState({ open: false, freeze: true });
  const [freezeReason, setFreezeReason] = useState('');
  const [processing, setProcessing] = useState(false);

  const fetchDeals = useCallback(async () => {
    try {
      // Fetch deals - for now using admin stats endpoint
      const statsRes = await api.get('/admin/stats');
      
      // Also try to get actual deals list
      // This would ideally be: GET /v2/deals
      const dealsData = statsRes.data?.recent_deals || [];
      setDeals(dealsData);
      
    } catch (error) {
      console.error('Failed to fetch deals:', error);
      toast.error('Failed to load deals');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTimeline = useCallback(async (dealId) => {
    if (!dealId) return;
    
    setTimelineLoading(true);
    try {
      // Fetch activity logs for this deal
      const activityRes = await api.get(`/admin/entity/DEAL/${dealId}/history`);
      const events = activityRes.data?.logs || [];
      
      // Sort by timestamp descending
      events.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      
      setTimeline(events);
    } catch (error) {
      console.error('Failed to fetch timeline:', error);
      toast.error('Failed to load deal timeline');
      setTimeline([]);
    } finally {
      setTimelineLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDeals();
  }, [fetchDeals]);

  useEffect(() => {
    if (selectedDeal) {
      fetchTimeline(selectedDeal.id);
    }
  }, [selectedDeal, fetchTimeline]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchDeals();
    if (selectedDeal) {
      await fetchTimeline(selectedDeal.id);
    }
    setRefreshing(false);
    toast.success('Data refreshed');
  };

  const handleFreezeAction = async () => {
    if (!selectedDeal || !freezeReason) {
      toast.error('Please provide a reason');
      return;
    }
    
    setProcessing(true);
    try {
      await api.post('/admin/controls/freeze-listing', {
        property_id: selectedDeal.property_id,
        deal_id: selectedDeal.id,
        reason: freezeReason,
        freeze: freezeDialog.freeze
      });
      
      toast.success(`Deal ${freezeDialog.freeze ? 'frozen' : 'unfrozen'} successfully`);
      setFreezeDialog({ open: false, freeze: true });
      setFreezeReason('');
      
      // Refresh
      await fetchDeals();
      await fetchTimeline(selectedDeal.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    } finally {
      setProcessing(false);
    }
  };

  const handleOpenCondition = (conditionId) => {
    if (conditionId) {
      // Navigate to classifications tab with condition highlighted
      window.location.href = `/admin?tab=classifications&condition=${conditionId}`;
    }
  };

  const handleOpenDispute = (dealId) => {
    if (dealId) {
      // Open dispute details
      toast.info('Dispute management coming soon');
    }
  };

  // Filter deals
  const filteredDeals = deals.filter(deal => {
    const matchesSearch = !searchQuery || 
      deal.id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      deal.property_title?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesState = stateFilter === 'all' || deal.deal_state === stateFilter;
    
    return matchesSearch && matchesState;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="deal-timeline-viewer">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-400" />
            Deal Timeline Viewer
          </h3>
          <p className="text-sm text-muted-foreground">
            Unified view of all deal activities
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={refreshing}
          data-testid="refresh-timeline-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Deals List */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Input
              placeholder="Search deals..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 bg-white/5 border-white/10"
            />
            <Select value={stateFilter} onValueChange={setStateFilter}>
              <SelectTrigger className="w-[140px] bg-white/5 border-white/10">
                <SelectValue placeholder="State" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All States</SelectItem>
                <SelectItem value="deal_offer_accepted">Offer Accepted</SelectItem>
                <SelectItem value="form_f_signed">Form F Signed</SelectItem>
                <SelectItem value="deposit_collected">Deposit Collected</SelectItem>
                <SelectItem value="dispute_mode">Dispute Mode</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <ScrollArea className="h-[600px] pr-4">
            {filteredDeals.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Building2 className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No deals found</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredDeals.map((deal) => (
                  <DealCard
                    key={deal.id}
                    deal={deal}
                    onSelect={setSelectedDeal}
                    isSelected={selectedDeal?.id === deal.id}
                  />
                ))}
              </div>
            )}
          </ScrollArea>
        </div>

        {/* Timeline */}
        <div className="col-span-2 space-y-4">
          {selectedDeal ? (
            <>
              {/* Deal Info Header */}
              <Card className="border-white/10 bg-card/50">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium">{selectedDeal.property_title || 'Untitled Property'}</h4>
                      <p className="text-sm text-muted-foreground font-mono">
                        Deal: {selectedDeal.id?.slice(0, 8).toUpperCase()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={DEAL_STATE_COLORS[selectedDeal.deal_state]}>
                        {selectedDeal.deal_state?.replace(/_/g, ' ')}
                      </Badge>
                      
                      {/* Admin Actions */}
                      <Button
                        size="sm"
                        variant="outline"
                        className={selectedDeal.is_frozen 
                          ? "border-green-600/50 text-green-400 hover:bg-green-950/30"
                          : "border-red-600/50 text-red-400 hover:bg-red-950/30"
                        }
                        onClick={() => setFreezeDialog({ open: true, freeze: !selectedDeal.is_frozen })}
                        data-testid="freeze-deal-btn"
                      >
                        {selectedDeal.is_frozen ? (
                          <><Unlock className="w-3 h-3 mr-1" /> Unfreeze</>
                        ) : (
                          <><Lock className="w-3 h-3 mr-1" /> Freeze</>
                        )}
                      </Button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-4 gap-4 mt-4 text-sm">
                    <div>
                      <p className="text-muted-foreground text-xs">Agreed Price</p>
                      <p className="font-medium">{formatCurrency(selectedDeal.agreed_price)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">Buyer</p>
                      <p className="font-medium truncate">{selectedDeal.buyer_email || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">Seller</p>
                      <p className="font-medium truncate">{selectedDeal.seller_email || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">Created</p>
                      <p className="font-medium">{formatDate(selectedDeal.created_at)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Timeline Events */}
              <Card className="border-white/10 bg-card/50">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Clock className="w-4 h-4 text-blue-400" />
                    Activity Timeline
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[450px] pr-4">
                    {timelineLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <RefreshCw className="w-5 h-5 animate-spin text-muted-foreground" />
                      </div>
                    ) : timeline.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <Clock className="w-8 h-8 mx-auto mb-2 opacity-30" />
                        <p className="text-sm">No activity recorded</p>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        {timeline.map((event, idx) => (
                          <TimelineEvent
                            key={event.timestamp + idx}
                            event={event}
                            onOpenCondition={handleOpenCondition}
                            onOpenDispute={handleOpenDispute}
                          />
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>
            </>
          ) : (
            <div className="flex items-center justify-center h-[600px] text-muted-foreground">
              <div className="text-center">
                <Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Select a deal to view its timeline</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Freeze/Unfreeze Dialog */}
      <Dialog open={freezeDialog.open} onOpenChange={(open) => !open && setFreezeDialog({ open: false, freeze: true })}>
        <DialogContent className="bg-card border-white/10">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {freezeDialog.freeze ? (
                <><Lock className="w-5 h-5 text-red-400" /> Freeze Deal</>
              ) : (
                <><Unlock className="w-5 h-5 text-green-400" /> Unfreeze Deal</>
              )}
            </DialogTitle>
            <DialogDescription>
              {freezeDialog.freeze 
                ? 'This will pause all activities on this deal. Use for disputes or investigations.'
                : 'This will resume normal deal activities.'
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="p-3 rounded bg-white/5 border border-white/10 mb-4">
              <p className="text-sm">
                <span className="text-muted-foreground">Deal:</span>{' '}
                <span className="font-mono text-primary">
                  {selectedDeal?.id?.slice(0, 8).toUpperCase()}
                </span>
              </p>
            </div>
            
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">
                Reason (Required)
              </label>
              <Textarea
                placeholder="Enter reason for this action..."
                value={freezeReason}
                onChange={(e) => setFreezeReason(e.target.value)}
                className="bg-white/5 border-white/10"
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setFreezeDialog({ open: false, freeze: true })}
            >
              Cancel
            </Button>
            <Button
              onClick={handleFreezeAction}
              disabled={processing || !freezeReason}
              className={freezeDialog.freeze ? "bg-red-600 hover:bg-red-700" : "bg-green-600 hover:bg-green-700"}
              data-testid="confirm-freeze-btn"
            >
              {processing ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  {freezeDialog.freeze ? <Lock className="w-4 h-4 mr-2" /> : <Unlock className="w-4 h-4 mr-2" />}
                  {freezeDialog.freeze ? 'Freeze Deal' : 'Unfreeze Deal'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
